import { translate } from 'i18n';
import { logger } from 'monitor';
import { useCallback, useEffect, useState } from 'react';
import { isIOS } from '../../../native/utils/isIOS/isIOS';
import { useNotification } from '../../../notifications/hooks/useNotification/useNotification';

import { AspectRatioType, MIN_RESOLUTION } from '../../config';

const getHighestResolutionFromCamera = async (): Promise<{ width: number; height: number } | null> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: 'environment',
      },
    });

    const videoTrack = stream.getVideoTracks()[0];

    if ('getCapabilities' in videoTrack) {
      const capabilities = videoTrack.getCapabilities();
      logger.info('capabilities', capabilities);
      const maxWidth = capabilities.width?.max;
      const maxHeight = capabilities.height?.max;

      stream.getTracks().forEach((track) => track.stop());

      if (maxWidth && maxHeight) {
        return { height: maxHeight, width: maxWidth };
      }
    }
    stream.getTracks().forEach((track) => track.stop());
    return null;
  } catch (error) {
    logger.error('Error getting highest resolution from camera: ', error);
    return null;
  }
};

const getMaxResolutionWithAspectRatio = (width: number, height: number, aspectRatio: number) => {
  let maxW = width;
  let maxH = Math.floor(width / aspectRatio);

  if (maxH > height) {
    maxH = height;
    maxW = Math.floor(height * aspectRatio);
  }

  return [maxW, maxH];
};

export const calculateIdealDimensions = (
  highestResolution: {
    width: number;
    height: number;
  } | null,
  aspectRatio: AspectRatioType,
  isPortrait: boolean
): { idealHeight: number; idealWidth: number } => {
  let idealHeight: number;
  let idealWidth: number;

  if (highestResolution) {
    const [maxW, maxH] = getMaxResolutionWithAspectRatio(
      isPortrait ? highestResolution.height : highestResolution.width,
      highestResolution.height,
      aspectRatio
    );
    idealHeight = maxH;
    idealWidth = maxW;
    logger.info('maxW, maxH calculated', {
      idealHeight,
      idealWidth,
    });
  } else {
    idealHeight = MIN_RESOLUTION[aspectRatio].minHeight;
    idealWidth = MIN_RESOLUTION[aspectRatio].minWidth;

    logger.info('maxW, maxH fallback', {
      idealHeight,
      idealWidth,
    });
  }

  logger.info('maxW, maxH final', {
    idealHeight,
    idealWidth,
  });
  return { idealHeight, idealWidth };
};

const DEFAULT_ZOOM_LEVEL = 1; // less than 1 triggers ultra-wide cameras
const getCameraStream = async ({ aspectRatio, isPortrait }: { aspectRatio: AspectRatioType; isPortrait: boolean }) => {
  logger.info('getting camera stream with aspect ratio ' + aspectRatio);
  let bestCameraId = null;

  // Detect iOS vs Android
  const isDeviceIos = isIOS();
  logger.info('Platform detection', { isIOS: isDeviceIos });

  // Only do camera enumeration on Android
  try {
    const tempStream = await navigator.mediaDevices.getUserMedia({ video: true }); // When you call navigator.mediaDevices.enumerateDevices() without first requesting camera permission, the browser returns devices with empty or generic labels for privacy reasons.
    tempStream.getTracks().forEach((track) => track.stop());

    if (isDeviceIos) {
      logger.info('iOS detected, using browser default camera selection');
    } else {
      const devices = await navigator.mediaDevices.enumerateDevices();
      logger.info('enumerateDevices', { devices });
      const rearCameras = devices.filter(
        (device) =>
          device.kind === 'videoinput' &&
          device.label &&
          (device.label.toLowerCase().includes('back') ||
            device.label.toLowerCase().includes('rear') ||
            device.label.toLowerCase().includes('environment'))
      );
      logger.info('rearCameras', { rearCameras });

      if (rearCameras.length > 0) {
        bestCameraId = rearCameras[rearCameras.length - 1].deviceId;
        logger.info('Selected last rear camera', { deviceId: bestCameraId });
      } else {
        logger.info('No rear cameras found, using default camera', { devices });
      }
    }
  } catch (error) {
    logger.error('Error enumerating cameras:', error);
  }

  const highestResolution = await getHighestResolutionFromCamera();
  logger.info('highestResolution', { highestResolution });
  const { idealHeight, idealWidth } = calculateIdealDimensions(highestResolution, aspectRatio, isPortrait);
  logger.info('ideal dimensions', { idealHeight, idealWidth });

  const videoConstraints = {
    facingMode: 'environment',
    // the width and height are swapped because the device orientation is different from the camera sensor orientation
    height: {
      ideal: idealWidth,
      min: idealWidth,
    },
    width: {
      ideal: idealHeight,
      min: idealHeight,
    },
    ...(bestCameraId && { deviceId: bestCameraId }),
  };

  logger.info('videoConstraints', { videoConstraints });

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: videoConstraints,
  });

  const videoTrack = stream.getVideoTracks()[0];
  if (videoTrack && 'getCapabilities' in videoTrack) {
    const capabilities = videoTrack.getCapabilities();
    logger.info('zoom capabilities', { capabilities });
    if ('zoom' in capabilities) {
      try {
        await videoTrack.applyConstraints({
          advanced: [
            {
              // @ts-expect-error zoom is not in the type definition but it is a valid constraint
              zoom: DEFAULT_ZOOM_LEVEL,
            },
          ],
        });
      } catch (error) {
        logger.error('Failed to apply zoom constraint:', error);
      }
    }
  }
  logger.info('Camera device settings: ', stream?.getVideoTracks()[0].getSettings());
  return stream;
};

export const useCameraStream = ({ aspectRatio, isPortrait }: { aspectRatio: AspectRatioType; isPortrait: boolean }) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const { open } = useNotification();
  const [isLoading, setIsLoading] = useState(true);
  const [flashEnabled, setFlashEnabled] = useState(false);

  // Warning! This will not work in strict mode!
  const releaseStream = useCallback(() => {
    if (stream) {
      logger.info('Releasing camera stream');
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  }, [stream]);

  const toggleFlash = useCallback(async () => {
    if (!stream) return;

    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return;

    try {
      const capabilities = videoTrack.getCapabilities();
      if ('torch' in capabilities && capabilities.torch) {
        const newFlashState = !flashEnabled;
        await videoTrack.applyConstraints({
          advanced: [
            {
              // @ts-expect-error torch is not in the type definition but it is a valid constraint
              torch: newFlashState,
            },
          ],
        });
        setFlashEnabled(newFlashState);
        logger.info('Flash toggled', { flashEnabled: newFlashState });
      } else {
        logger.warn('Flash/torch not supported on this device');
      }
    } catch (error) {
      logger.error('Failed to toggle flash:', error);
    }
  }, [stream, flashEnabled]);

  useEffect(() => {
    let isMounted = true;

    const initStream = async () => {
      try {
        const fetchedStream = await getCameraStream({ aspectRatio, isPortrait });

        if (isMounted) {
          setStream(fetchedStream);
        }
      } catch (error) {
        logger.error('Failed to get camera stream', error);
        open({
          description: translate('backlog.cameraGuidance.useCameraStream.notifications.refreshAndAllowCamera', {
            namespace: 'features',
          }),
          message: translate('backlog.cameraGuidance.useCameraStream.notifications.cannotUseCamera', {
            namespace: 'features',
          }),
          type: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    };

    initStream();

    return () => {
      isMounted = false;
      releaseStream();
    };
    // disable exhaustive-deps because we want to re-run this effect only when aspectRatio or isPortrait changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspectRatio, isPortrait]);

  return {
    flashEnabled,
    isLoading,
    releaseStream,
    stream,
    toggleFlash,
  };
};
